<template>
  <v-card class="CardLinearGraphics mt-4" text elevation="0">
      <v-card-title class="pa-0 pb-4">{{$t("CardLinearGraphics.temporalView")}}</v-card-title>
      <v-container fluid class="Content d-flex pa-0">
      <div class="Options overflow-hidden pr-1 mr-3">
        <h3 class="headline mt-2 mb-3">{{$t("CardLinearGraphics.variables")}}</h3>
        <div
          v-for="(key) in getOptionsKeys"
          :key="key"
          :class="{Opt: true, Active: options[key].checked}"
          @click="toggleOpt(key)"
        >
          <v-icon v-if="options[key].checked" small color="#0098E1" class="mt-1 fill-height">radio_button_checked</v-icon>
          <v-icon v-else small class="mt-1" >radio_button_unchecked</v-icon>
          <p class="subheading d-inline-block ml-1 mb-2">{{options[key].label}}</p>
        </div>
      </div>
      <div class="Graphic fill-height d-flex align-center justify-center px-5">
        <span v-if="!chartData" class="display-1 text-center grey--text">{{$t("CardLinearGraphics.dataNotAvailable")}}</span>
        <template v-else>
          <div v-if="checkedKey === 'irrigation'">
            <apexchart height="240" width="750" type="area" :options="chartData.chartOptions" :series="chartData.values"></apexchart>
            <span class="YReference">{{($t("CardLinearGraphics.mmUnit")).toUpperCase()}}</span>
          </div>
          <div v-else-if="checkedKey === 'movement'">
            <apexchart height="240" width="750" type="line" :options="chartData.chartOptions" :series="chartData.values"></apexchart>
          </div>
        </template>
      </div>
    </v-container>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import i18n from '@/lib/i18n'
import { setZonedTimeFromUTCISO, getFormatDateFromISO } from '../../lib/time-transformations'

const parseTooltipDate = (date) => {
  const dateConfig = "dd'/'LL',' hh':'mm a"
  let dateString = getFormatDateFromISO(date, dateConfig)
  if (dateString.split(',')[1].includes('12') && dateString.includes('AM')) {
    dateString = dateString.substr(0) + '00' + dateString.substr(9)
  }
  return dateString.replace(/\.\s|\./g, '')
}

const parseIrrigationData = (csvData) => {
  let csvLines = csvData.split('\n') // CSV Input
  const arrValues = []
  const arrDates = []

  for (let j = 1; j < csvLines.length; j++) {
    let line = csvLines[j].split(';')
    if (line.length === 2 && line[0].length > 0 && line[0].length > 0) {
      const [rawDate, rawTime] = line[0].split(' ')
      const [year, month, day] = rawDate.split('-')
      const [hours, minutes] = rawTime.split(':')
      let date = setZonedTimeFromUTCISO(new Date(Date.UTC(year, month - 1, day, hours, minutes)).toISOString())
      arrDates.push(date)
      let val = line[1].replace(',', '.')
      arrValues.push(parseFloat(val))
    }
  }

  return {
    arrValues,
    arrDates
  }
}
const getIrrigationChartData = ({arrValues, arrDates}) => {
  return {
    values: [{
      name: '',
      data: arrValues
    }],
    chartOptions: {
      chart: {
        type: 'area',
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        width: 750,
        height: 240
      },
      colors: ['#0098E1'],
      fill: {
        colors: ['#0098E1'],
        type: 'solid'
      },
      legend: {
        show: false
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth',
        colors: ['#FFFFFF00']
      },
      xaxis: {
        type: 'datetime',
        categories: arrDates,
        labels: {
          show: true,
          datetimeFormatter: {
            day: 'dd/MM'
          }
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        labels: {
          show: true,
          maxWidth: 30
        }
      },
      tooltip: {
        inverseOrder: true,
        custom ({series, seriesIndex, dataPointIndex}) {
          const dateString = parseTooltipDate(arrDates[dataPointIndex])
          const tooltip = series[seriesIndex][dataPointIndex] || series[seriesIndex][dataPointIndex] === 0 ? `
            <div class="LinearTooltip">
              <p class="TooltipValue">
                <span>${series[seriesIndex][dataPointIndex]}</span>
                <span class="ValueSuff">${i18n.t('CardLinearGraphics.mmUnit')}</span>
              </p>
              <p class=>${dateString}</p>
            </div>
          ` : ''
          return tooltip
        },
        x: {
          format: 'dd/MM HH:mm'
        },
        y: {
          formatter (val) {
            return val + ` ${i18n.t('CardLinearGraphics.mmUnit')}`
          },
          title: {
            formatter () {
              return ''
            }
          }
        },
        marker: {
          show: false
        }
      }
    }
  }
}

const parseMovementData = (csvData) => {
  let csvLines = csvData.split('\n') // CSV Input
  const arrDates = []
  const arrIrrigation = []
  const arrStop = []
  const arrFault = []
  const arrMovement = []

  for (let j = 1; j < csvLines.length; j++) {
    let line = csvLines[j].split(';')
    if (line.length === 5 && line[0].length > 0) {
      const [rawDate, rawTime] = line[0].split(' ')
      const [year, month, day] = rawDate.split('-')
      const [hours, minutes] = rawTime.split(':')
      let date = setZonedTimeFromUTCISO(new Date(Date.UTC(year, month - 1, day, hours, minutes)).toISOString())
      arrDates.push(date)

      let irrVal = line[1] && line[1].replace(',', '.')
      arrIrrigation.push((irrVal) ? parseFloat(irrVal) : null)

      let stopVal = line[2] && line[2].replace(',', '.')
      arrStop.push((stopVal) ? parseFloat(stopVal) : null)

      let faultVal = line[3] && line[3].replace(',', '.')
      arrFault.push((faultVal) ? parseFloat(faultVal) : null)

      let moveVal = line[4] && line[4].replace(',', '.')
      arrMovement.push((moveVal) ? parseFloat(moveVal) : null)
    }
  }

  return {
    arrDates,
    arrIrrigation,
    arrStop,
    arrFault,
    arrMovement
  }
}
const getMovementChartData = ({arrDates, arrIrrigation, arrStop, arrFault, arrMovement}) => {
  const colors = ['#00D166', '#D1D1D1', '#F93A2F', '#4F5AB0']

  return {
    values: [{
      name: i18n.t('CardLinearGraphics.irrigation'),
      data: arrIrrigation
    }, {
      name: i18n.t('CardLinearGraphics.off'),
      data: arrStop
    }, {
      name: i18n.t('CardLinearGraphics.fault'),
      data: arrFault
    }, {
      name: i18n.t('CardLinearGraphics.movement'),
      data: arrMovement
    }],
    chartOptions: {
      chart: {
        type: 'line',
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        animations: {
          enabled: false
        },
        width: 750,
        height: 240
      },
      fill: {
        type: 'solid'
      },
      legend: {
        show: false
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'straight',
        colors
      },
      markers: {
        size: 0,
        showNullDataPoints: false,
        colors
      },
      xaxis: {
        type: 'datetime',
        categories: arrDates,
        labels: {
          show: true,
          datetimeFormatter: {
            day: 'dd/MM'
          }
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        min: 0,
        max: 360,
        tickAmount: 4,
        labels: {
          show: true,
          maxWidth: 30,
          formatter (val) {
            return val.toFixed(0)
          }
        }
      },
      tooltip: {
        shared: false,
        inverseOrder: true,
        custom ({series, seriesIndex, dataPointIndex, w}) {
          const dateString = parseTooltipDate(arrDates[dataPointIndex])
          const tooltip = series[seriesIndex][dataPointIndex] || series[seriesIndex][dataPointIndex] === 0 ? `
            <div class="LinearTooltip">
              <p class="TooltipValue">
                <span>${series[seriesIndex][dataPointIndex].toFixed(0)}°</span>
              </p>
              <p class=>${w.globals.seriesNames[seriesIndex]}</p>
              <p class=>${dateString}</p>
            </div>
          ` : ''

          return tooltip
        },
        x: {
          format: 'dd/MM HH:mm'
        },
        y: {
          formatter (val) {
            return val + '°'
          },
          title: {
            formatter (val) {
              return val + ':'
            }
          }
        },
        marker: {
          show: false
        }
      }
    }
  }
}

export default {
  data () {
    const getOptionsInitialData = () => ({
      irrigation: {
        label: this.$t('CardLinearGraphics.irrigation'),
        checked: false
      },
      movement: {
        label: this.$t('CardLinearGraphics.movement'),
        checked: false
      }
    })
    return {
      options: getOptionsInitialData()
    }
  },
  props: ['location', 'title'],
  components: {
    apexchart: VueApexCharts
  },
  methods: {
    toggleOpt (key) {
      if (!this.options[key].checked) {
        for (let opt in this.options) {
          this.options[opt].checked = false
        }

        this.options[key].checked = true
      }
    }
  },
  mounted () {
    setTimeout(() => {
      this.toggleOpt('irrigation')
    }, 100)
  },
  computed: {
    getOptionsKeys () {
      const keys = []
      for (let key in this.options) {
        keys.push(key)
      }
      return keys
    },
    checkedKey () {
      for (let key in this.options) {
        if (this.options[key].checked) {
          return key
        }
      }
      return null
    },
    checkedOpt () {
      for (let key in this.options) {
        if (this.options[key].checked) {
          return this.options[key]
        }
      }
    },
    chartData () {
      switch (this.checkedKey) {
        case 'irrigation':
          if (!this.location.linearGraphics.irrigation_data) {
            return null
          }
          return getIrrigationChartData(parseIrrigationData(this.location.linearGraphics.irrigation_data))
        case 'movement':
          if (!this.location.linearGraphics.movement_data) {
            return null
          }
          return getMovementChartData(parseMovementData(this.location.linearGraphics.movement_data))
        default:
          return {chartOptions: {}, values: []}
      }
    }
  }
}
</script>

<style>

</style>


// WEBPACK FOOTER //
// CardLinearGraphics.vue?89c37ebc